/* eslint-disable */
// @ts-nocheck

/* eslint-enable prettier/prettier */
/* eslint-enable @typescript-eslint/no-unused-vars */
/* eslint-enable import/no-unresolved */

import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core";
import PropTypes from "prop-types";
import ReactWordcloud from "react-wordcloud";

import { HIGH_CONTRAST_COLOR_FOR_FG_ON_WHITE_BG_TEXT } from "@untyped-shared/colors";

import AccessibleTable from "./AccessibleTable";

// Hook to track rem size changes using ResizeObserver on the existing container
const useRemInPixels = (containerRef: React.RefObject<HTMLDivElement>) => {
  const [remInPixels, setRemInPixels] = useState(() => {
    // Default for Jest tests
    if (typeof document === "undefined") return 10;

    const fontSize = getComputedStyle(document.documentElement).fontSize;
    return parseFloat(fontSize) || 10;
  });

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !window.ResizeObserver ||
      !containerRef.current
    ) {
      return;
    }

    const container = containerRef.current;

    // Create ResizeObserver to watch the existing container
    const observer = new ResizeObserver(() => {
      // When container resizes (due to rem changes), re-measure rem
      const fontSize = getComputedStyle(document.documentElement).fontSize;
      const measuredRem = parseFloat(fontSize) || 10;

      if (Math.abs(measuredRem - remInPixels) > 0.1) {
        setRemInPixels(measuredRem);
      }
    });

    // Start observing the existing container (which uses rem units)
    observer.observe(container);

    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, [containerRef, remInPixels]);

  return remInPixels;
};

const useStyles = makeStyles({
  svgWidth: {
    maxWidth: "100%",
  },
});

function WordsCloudComponent({ words, textSize = 1.0 }) {
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const remInPixels = useRemInPixels(containerRef);

  // Define base font sizes in rem
  let minFontSizeRem;
  let maxFontSizeRem;

  switch (true) {
    case screen.width < 320:
      maxFontSizeRem = 1.0; // 1rem
      minFontSizeRem = 1.0; // 1rem
      break;
    case screen.width < 450:
      maxFontSizeRem = 1.5; // 1.5rem
      minFontSizeRem = 1.5; // 1.5rem
      break;
    case screen.width > 450:
      maxFontSizeRem = 5.5; // 5.5rem
      minFontSizeRem = 2.0; // 2rem
      break;
  }

  // Convert rem to pixels for the library, scaled by textSize
  const minFontSize = minFontSizeRem * textSize * remInPixels;
  const maxFontSize = maxFontSizeRem * textSize * remInPixels;

  const wordsCloudOptions = {
    enableTooltip: true,
    enableOptimizations: true,
    colors: HIGH_CONTRAST_COLOR_FOR_FG_ON_WHITE_BG_TEXT,
    deterministic: true,
    fontFamily: "Open Sans",
    fontSizes: [minFontSize, maxFontSize],
    rotations: 2,
    rotationAngles: [0, 90],
    svgAttributes: {
      class: classes.svgWidth,
    },
  };

  const callbacks = {
    getWordTooltip: (word) => `${word.tooltip} (${word.value})`,
  };

  // This is a workaround for the fact that the wordcloud library
  // doesn't work in Jest. It's not a problem for the app itself
  const runningInTestEnv = Util.inJest;

  // Scale the cloud container height based on textSize
  const scaledHeight = 50.0 * textSize;

  return (
    <div
      ref={containerRef}
      className="words-cloud-component"
      style={{ margin: "0 auto", height: `${scaledHeight}rem` }}
    >
      {!runningInTestEnv && (
        <ReactWordcloud
          words={words}
          options={wordsCloudOptions}
          callbacks={callbacks}
        />
      )}
    </div>
  );
}

WordsCloudComponent.propTypes = {
  words: PropTypes.array.isRequired,
  textSize: PropTypes.number,
};

const WordsCloudMemoized = React.memo(
  WordsCloudComponent,
  (prevProps, nextProps) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps);
  },
);

const truncateString = (string = "", maxLength = 12) =>
  string.length > maxLength ? `${string.substring(0, maxLength)}…` : string;

type TProps = {
  answers: { value: string; count: number }[];
  showAccessibleView: boolean;
  textSize?: number;
};

function WordsCloud({ answers, showAccessibleView, textSize = 1.0 }: TProps) {
  //debugger;

  // TODO rename "value", word cloud uses "value" as count

  if (showAccessibleView) {
    const tableData = answers.map((answer) => ({
      text: answer.value, // Full text for accessible table
      value: answer.count,
    }));
    return <AccessibleTable list={tableData} textSize={textSize} />;
  }

  const words = answers.map((answer) => ({
    text: truncateString(answer.value),
    value: answer.count,
    tooltip: answer.value,
  }));
  return <WordsCloudMemoized words={words} textSize={textSize} />;
}

WordsCloud.propTypes = {
  answers: PropTypes.array.isRequired,
};

export default WordsCloud;
